import { Injectable, inject } from '@angular/core';
import { SourcingProductsFilterService } from '@clubhub/ignite/services/products/products-filter/sourcing-products-filter.service';
import { BridgingProductsHelper } from '@shared/helpers/bridging-products.helper';
import {
  Availability,
  BridgingProduct,
  BridgingPurchaserType,
  LendingTypeCode,
  Product,
  ProductLength,
  ProductLengthExtended,
  ProductType,
  RepaymentMethod,
} from '@shared/models';
import { LendingTypeService } from '@shared/services/lending-type.service';
import uniqBy from 'lodash-es/uniqBy';

import { productFiltersNames } from '@clubhub/constants';
import * as productFiltersConstants from '@clubhub/ignite/constants/product-filters';
import { IgniteCommonDataService } from '@clubhub/ignite/services/ignite.store';
import { ProductFeeType } from '@shared/constants';
import { productFilterGroupTitles, tailoredProductTitles, topSlicingFilterTitle } from '@shared/constants/filter-preference-options';
import { IgniteHelperService } from '@shared/services/ignite.helper';

export interface FilterOptionsContext<T> {
  products: T[] | null;
  initialPeriod?: number;
}

export interface ProductsFilterContext {
  repaymentMethod?: RepaymentMethod;
}

export interface ProductFilterDefinition<T> {
  title: string;
  isMatchFunc?: (product: T, conditionValue?: unknown, conditionValue2?: unknown) => boolean;
  isUnknownFunc?: (product: T) => boolean;
  isExcludeFunc?: (product: T) => boolean;
  visible?: (ctx: FilterOptionsContext<T>) => boolean;
  /** Override the visible status of the checkbox. Does not affect the generated filter function, only the UI. */
  overrideChecked?: (ctx: FilterOptionsContext<T>) => boolean | null;
  onActivateMessage?: string;
  hasSubFilters?: boolean;
  hasNumericValue?: boolean;
  numericFilterValidators?: ((p: number | undefined) => string | undefined)[];
  numericFilterDescription?: string;
  numericFilterPrefix?: string;
  isRangeFilter?: boolean;
  rangeFilterUnits?: string;
  itemFactory?: (ctx: FilterOptionsContext<T>) => ProductFilterDefinition<T>[];
  isMatchFuncFactory?: (key: string) => (product: T) => boolean;
}

export type ProductFilterGroupDefinition<T> = {
  title: string;
  requestObjKey: string;
  isIncludeExcludeFilter?: boolean;
  itemOperator: 'and' | 'or';
  groupInfoText?: string;
  beforeText?: string;
  hasSubFilters?: boolean;
  disabled?: (ctx: FilterOptionsContext<T>) => boolean;
} & (
  | {
    items: ProductFilterDefinition<T>[];
  }
  | {
    itemFactory: (ctx: FilterOptionsContext<T>) => ProductFilterDefinition<T>[];
    isMatchFuncFactory: (key: string) => (product: T) => boolean;
    isUnknownFuncFactory?: (key: string) => (product: T) => boolean;
  }
);

const topSlicingMessage =
  'When you apply this filter, it will display lenders who have set their criteria to "yes" and "refer." ' +
  'To see a detailed breakdown of these criteria, you would need to include the Top Slicing criteria in your search.';

@Injectable({
  providedIn: 'root',
})
export class ProductsFilterHelper {
  private readonly lendingTypeService = inject(LendingTypeService);
  private readonly bridgingProductsHelper = inject(BridgingProductsHelper);
  private readonly sourcingProductsFilterService = inject(SourcingProductsFilterService);
  private readonly igniteHelperService = inject(IgniteHelperService);
  private readonly igniteCommonDataService = inject(IgniteCommonDataService);

  //Initial period specific rounding. Use those bands to calculate the range on months which correspond to a given
  // full year. E.g. a 2-year period would start from (24 - lowerInitialPeriodBound) to (24 + higherInitialPeriodBound)
  public lowerInitialPeriodBound = 2;
  public higherInitialPeriodBound = 9;

  public readonly productTypeFilters = new Map<ProductType, (p: Product) => boolean>([
    [
      ProductType.Standard,
      p =>
        p.retirementInterestOnlyRio !== true &&
        ![p.sharedOwnership, p.rightToBuy, p.sharedEquity, p.selfBuild, p.jointBorrowerSoleProprietor].includes(Availability.OnlyAvailable),
    ],
    [ProductType.SharedOwnership, p => p.sharedOwnership !== Availability.No],
    [ProductType.RightToBuy, p => p.rightToBuy !== Availability.No],
    [ProductType.SharedEquity, p => p.sharedEquity !== Availability.No],
    [ProductType.SelfBuild, p => p.selfBuild !== Availability.No],
    [ProductType.RetirementInterestOnly, p => p.retirementInterestOnlyRio !== false],
    [ProductType.JointBorrowerSoleProprietor, p => p.jointBorrowerSoleProprietor !== Availability.No],
  ]);

  /**
   * For the given period in years, works out the min and max months (inclusive) that a product should have to be within
   * that period.
   */
  public getProductLengthBounds(periodYears: number): [number, number] {
    const periodInMonths = periodYears * 12;
    const periodLowerLimit = periodInMonths - this.lowerInitialPeriodBound;
    const periodUpperLimit = periodInMonths + this.higherInitialPeriodBound;
    return [periodLowerLimit, periodUpperLimit];
  }

  private initialPeriodFilter(period: number | null): (p: Product) => boolean {
    // A 'null' period indicates a for-term product
    if (period === null || period === 0) {
      return p => p.initialPeriod === null;
    }

    // If a non-null period is given:
    // Find the current period in years with the upper and lower bounds of the given months.
    // Return a filter function that finds the products within the upper and lower bounds.
    // E.G. for a 10-year filter 10 * 12 = 120 months and lower bound = 2 and higher bound equal 9
    // making the lower 118 and upper 129 month bounds.
    // meaning we return products which are more or equal to 118 and less or equal to 129 months.

    const [periodLowerLimit, periodUpperLimit] = this.getProductLengthBounds(period);

    return p => p.initialPeriod !== null && p.initialPeriod >= periodLowerLimit && p.initialPeriod <= periodUpperLimit;
  }

  private get productTypeCode(): LendingTypeCode {
    return ((this.lendingTypeService as LendingTypeService).lendingType?.code as LendingTypeCode) ?? LendingTypeCode.Res;
  }

  public resetSourcingFilters(products?: Product[] | null) {
    this.sourcingProductsFilterService.reset(products);
  }

  public clearSourcingFilters() {
    this.sourcingProductsFilterService.clear();
  }

  private titleCase(text: string): string {
    return text
      .split(' ')
      .map(word => word[0].toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  public resAndBtlProductFilterDefinitions(isBtl: boolean): ProductFilterGroupDefinition<Product>[] {
    return [
      {
        requestObjKey: 'rateType',
        title: productFilterGroupTitles.rateType,
        itemOperator: 'or',
        itemFactory: ({ products }) =>
          uniqBy(
            (products ?? []).map(x => x.initialRateType),
            t => t?.toLowerCase(),
          )
            .filter(type => !!type)
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            .map(type => (type !== 'Tracker (SONIA)' ? this.titleCase(type!) : type))
            .sort((a, b) => a.localeCompare(b))
            .map(type => ({ key: type, title: type }) as ProductFilterDefinition<Product>),
        isMatchFuncFactory: key => product => {
          const normalizedKey = key.toLowerCase();
          const normalizedProductType = (product.initialRateType ?? '').toLowerCase();

          return normalizedKey === 'tracker'
            ?
            normalizedProductType === 'tracker' || normalizedProductType === 'tracker (sonia)'
            : normalizedProductType === normalizedKey;
        },
      },
      {
        requestObjKey: 'initialPeriod',
        title: productFiltersNames.initialProductPeriod,
        itemOperator: 'or',
        groupInfoText: 'Covers 2 months before and 9 months after the selected term (e.g. 2-year filter = 22-33 months)',
        disabled: isBtl
          ? () => !!this.igniteCommonDataService.affordabilityModel && !this.igniteCommonDataService.isAffordabilityModelFromProductModel
          : ({ initialPeriod }) => initialPeriod !== undefined && initialPeriod !== 0,
        items: [
          // Note that products with a month count not divisible by 12 should match for either of their nearest years.
          // For example, an 18-month period should show up for 1 year products (12 months) OR
          // 2 year products (24 months)
          ...this.igniteHelperService.initialPeriodRangeOptions.map(
            length =>
              ({
                title: this.getInitialProductPeriodFilterName(length),
                isMatchFunc: this.initialPeriodFilter(length),
                overrideChecked: isBtl ? undefined : ({ initialPeriod }) => (initialPeriod === undefined ? null : initialPeriod === length),
              }) as ProductFilterDefinition<Product>,
          ) as any[],
          {
            title: this.igniteHelperService.forTermFilterName,
            isMatchFunc: this.initialPeriodFilter(0),
            overrideChecked: isBtl ? undefined : ({ initialPeriod }) => (initialPeriod === undefined ? null : initialPeriod === 0),
          },
          {
            title: this.igniteHelperService.customFilterName,
            isMatchFunc: (product, fromValue: number, toValue: number) => {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              return product.initialPeriod! >= fromValue && product.initialPeriod! <= toValue;
            },
            overrideChecked: isBtl ? undefined : ({ initialPeriod }) => (initialPeriod === undefined ? null : initialPeriod === 0),
            isRangeFilter: true,
            rangeFilterUnits: '',
            numericFilterValidators: [
              (v: number | undefined | null) => {
                if (v === undefined || v === null) {
                  return 'From and To are required';
                }
              },
            ],
          },
        ],
      },
      // If either product features or tailored products are changed, ensure that the filter logic on the backend
      // is also changed in MSS.SmartrCriteria/Extensions/ProductFilterExtensions.cs
      {
        requestObjKey: 'productPreferences',
        title: productFilterGroupTitles.productFeatures,
        itemOperator: 'and',
        items: [
          ...(isBtl
            ? ([
              {
                title: topSlicingFilterTitle,
                isMatchFunc: p => p.lenderAllowsTopSlicing === true,
                onActivateMessage: topSlicingMessage,
              },
            ] satisfies ProductFilterDefinition<Product>[])
            : []) as any[],
          {
            title: 'Cashback',
            isMatchFunc: p => !!p.cashback && p.cashback > 0,
            isUnknownFunc: p => p.cashback === null,
          },
          {
            title: 'ERC\'s applied',
            isMatchFunc: p => p.earlyRepaymentChargesErcApplied === true,
          },
          {
            title: 'No ERC\'s applied',
            isMatchFunc: p => !p.earlyRepaymentChargesErcApplied,
          },
          {
            title: 'Free legal',
            isMatchFunc: p => p.freeLegal === true || p.productTransferEligible === true,
          },
          {
            title: 'Free valuation',
            isMatchFunc: p => p.freeVal === true || p.fees[ProductFeeType.Valuation] === 0 || p.productTransferEligible === true,
          },
          {
            title: 'Max arrangement fee',
            hasNumericValue: true,
            numericFilterDescription: 'Enter the maximum arrangement fee to filter the products',
            numericFilterPrefix: '£',
            isMatchFunc: (product, maxArrangementFee: number) => {
              if (maxArrangementFee) {
                const arrangementFee = product.fees?.[ProductFeeType.Arrangement] ?? 0;
                return arrangementFee <= maxArrangementFee;
              }

              return false;
            },
            numericFilterValidators: [
              (v: number | undefined | null) => {
                if (v === undefined || v === null) {
                  return 'Enter a value';
                }

                if (v < 1) {
                  return 'Must be greater than 0';
                }
              },
            ],
          },
          {
            title: 'No arrangement fee',
            isMatchFunc: p => !(ProductFeeType.Arrangement in p.fees),
          },
          {
            title: 'No booking fee',
            isMatchFunc: p => !(ProductFeeType.Booking in p.fees),
          },
          {
            title: 'Overpayments allowed',
            isMatchFunc: p => p.overpaymentsAllowed === true,
          },
          {
            title: 'Portable',
            isMatchFunc: p => p.portable === true,
          },
          {
            title: 'Valuation fee',
            isMatchFunc: p => ProductFeeType.Valuation in p.fees && (p.fees[ProductFeeType.Valuation] ?? 0) > 0,
          },
        ],
      },
      {
        requestObjKey: 'productPreferences',
        title: productFilterGroupTitles.tailoredProducts,
        beforeText:
          'When you apply a filter, we\'ll show the products that match.' +
          ' To confirm if a lender will approve, complete the required steps in the Criteria section.',
        isIncludeExcludeFilter: true,
        hasSubFilters: true,
        itemOperator: 'and',
        items: [
          {
            title: tailoredProductTitles.directOnly,
            isMatchFunc: p => p.direct === true,
            isUnknownFunc: () => false,
            isExcludeFunc: p => p.direct !== true,
          },
          {
            title: productFiltersConstants.expatNotInUkFilterTitle,
            ...this.sourcingProductsFilterService.sourcingCriteriaFilterFor(productFiltersConstants.expatNotInUkFilterValue),
          },
          {
            title: productFiltersConstants.greenEcoFilterTitle,
            ...this.sourcingProductsFilterService.sourcingCriteriaFilterFor(productFiltersConstants.greenEcoFilterValue),
          },
          {
            title: productFiltersConstants.offsetFilterTitle,
            ...this.sourcingProductsFilterService.sourcingCriteriaFilterFor(productFiltersConstants.offsetFilterValue),
            visible: () => this.productTypeCode === LendingTypeCode.Res,
          },
          {
            title: tailoredProductTitles.ownNew,
            isMatchFunc: p => !!p.ownNew && [Availability.AlsoAvailable, Availability.OnlyAvailable].includes(p.ownNew),
            isExcludeFunc: p => p.ownNew !== Availability.OnlyAvailable,
            visible: () => this.productTypeCode === LendingTypeCode.Res,
          },
          {
            title: productFiltersConstants.regulatedBTLFilterTitle,
            ...this.sourcingProductsFilterService.sourcingCriteriaFilterFor(productFiltersConstants.regulatedBTLFilterValue),
            visible: () => this.productTypeCode === LendingTypeCode.Btl,
          },
          {
            title: tailoredProductTitles.retrofit,
            isMatchFunc: p => !!p.retroFit && [Availability.AlsoAvailable, Availability.OnlyAvailable].includes(p.retroFit),
            isExcludeFunc: p => p.retroFit !== Availability.OnlyAvailable,
          },
          {
            title: productFiltersConstants.secondResidentialFilterTitle,
            ...this.sourcingProductsFilterService.sourcingCriteriaFilterFor(productFiltersConstants.secondResidentialFilterValue),
            visible: () => this.productTypeCode === LendingTypeCode.Res,
          },
          {
            title: tailoredProductTitles.packagers,
            hasSubFilters: true,
            itemFactory: ({ products }) =>
              uniqBy(
                (products ?? []).map(x => x.packager),
                t => t?.toLowerCase(),
              )
                .filter((type): type is string => !!type)

                .sort((a, b) => a.localeCompare(b))
                .map(type => ({ key: type, title: type }) as ProductFilterDefinition<Product>),
            isMatchFuncFactory: key => product => product.packager?.toLowerCase() === key.toLowerCase(),
          },
        ],
      },
    ];
  }

  public readonly bridgingProductFilterDefinitions: ProductFilterGroupDefinition<BridgingProduct>[] = [
    {
      // Bridging! Rename?
      requestObjKey: 'productType',
      title: 'Product type',
      itemOperator: 'or',
      itemFactory: ({ products }) =>
        uniqBy(
          (products ?? []).map(x => this.bridgingProductsHelper.productTypeToString(x.productType)),
          t => t,
        )
          .filter(type => !!type)
          .sort((a, b) => a.localeCompare(b))
          .map(type => ({ key: type, title: type }) as ProductFilterDefinition<BridgingProduct>),
      isMatchFuncFactory: key => product => this.bridgingProductsHelper.productTypeToString(product.productType) === key,
    },
    {
      requestObjKey: 'productPeriod',
      title: 'Product period',
      itemOperator: 'or',
      itemFactory: ({ products }) =>
        uniqBy(
          (products ?? []).map(x => this.bridgingProductsHelper.maxTermToString(x.maxTerm)),
          t => t,
        )
          .filter(type => !!type)
          .sort((a, b) => a.localeCompare(b))
          .map(type => ({ key: type, title: type }) as ProductFilterDefinition<BridgingProduct>),
      isMatchFuncFactory: key => product => this.bridgingProductsHelper.maxTermToString(product.maxTerm) === key,
    },
    {
      requestObjKey: 'productPreferences',
      title: 'Product features',
      itemOperator: 'and',
      items: [
        {
          title: 'Exit fee charges applied',
          isMatchFunc: p => p.exitFee,
        },
        {
          title: 'Dual legal representation',
          isMatchFunc: p => p.dualLegalRepresentation === true,
          isUnknownFunc: p => p.dualLegalRepresentation === null,
        },
        {
          title: 'Free legal',
          isMatchFunc: p => p.freeLegal === true,
          isUnknownFunc: p => p.freeLegal === null,
        },
        {
          title: 'Avm accepted',
          isMatchFunc: p => p.avmAccepted === true,
          isUnknownFunc: p => p.avmAccepted === null,
        },
      ],
    },
    {
      requestObjKey: 'productPreferences',
      title: 'Tailored products',
      isIncludeExcludeFilter: true,
      hasSubFilters: true,
      itemOperator: 'and',
      items: [
        {
          title: 'Expat not in UK',
          isMatchFunc: p => p.expatNotInUk === true,
          isExcludeFunc: p => p.expatNotInUk === false,
          isUnknownFunc: p => p.expatNotInUk === null,
        },
        {
          title: 'Limited company',
          isMatchFunc: p => p.purchaserTypes?.includes(BridgingPurchaserType.LimitedCompany),
          isUnknownFunc: p => p.purchaserTypes === null,
        },
        {
          title: 'Bridge to let',
          isMatchFunc: p => p.bridgeToLet === true,
          isExcludeFunc: p => p.bridgeToLet === false,
          isUnknownFunc: p => p.bridgeToLet === null,
        },
        {
          title: 'Portfolio landlord',
          isMatchFunc: p => p.portfolioLandlord === true,
          isExcludeFunc: p => p.portfolioLandlord === false,
          isUnknownFunc: p => p.portfolioLandlord === null,
        },
      ],
    },
  ];

  public getRequestModelInitialProductPeriod(filterNames: string[]) {
    if (filterNames.includes(this.igniteHelperService.customFilterName)) {
      return [ProductLengthExtended.Custom];
    }

    const productLengths: ProductLengthExtended[] = [];
    if (filterNames.includes(this.igniteHelperService.forTermFilterName)) {
      productLengths.push(ProductLengthExtended.ForTerm);
    }

    const numericFilters = filterNames.map(str => {
      const match = /\d+/.exec(str);
      const productLength = match ? Number.parseInt(match[0], 10) : null;
      if (productLength) {
        return this.igniteHelperService.mapNumberToProductLength(productLength);
      }

      return undefined;
    }) as ProductLengthExtended[];

    productLengths.push(...numericFilters);

    return productLengths.filter(i => !!i);
  }

  public getInitialProductPeriodFilterName(initialPeriod: number) {
    const productLength = this.igniteHelperService.mapNumberToProductLength(initialPeriod);
    return this.igniteHelperService.getInitialProductPeriodFilterName(productLength);
  }

  public getInitialProductPeriodFilter(productLength: ProductLength): string {
    return this.igniteHelperService.getInitialProductPeriodFilterName(productLength);
  }

  public getInitialProductPeriodFilters(productLengths: ProductLengthExtended[]): string[] {
    const filters = productLengths.map(productLength => {
      return this.igniteHelperService.getInitialProductPeriodFilterName(productLength)?.toString() || '';
    });

    return filters;
  }
}
